import { Link2, RefreshCw } from "lucide-react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDemandLinks, useRefreshDemandLinks } from "@/hooks/use-integration-solution-demands";

/**
 * Integrations → "Solution → Demand Links" tab. Read-only view of the
 * auto-synced solution_demand_link set (there is no manual CRUD — the
 * /demand-solution sync is the only writer). Each Solution's demands scope its
 * guidance forecast and hours actuals.
 */
export function DemandLinksPanel() {
  const demandLinks = useDemandLinks();
  const refresh = useRefreshDemandLinks();

  if (demandLinks.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (demandLinks.error) {
    return <ErrorState message={demandLinks.error.message} onRetry={() => demandLinks.refetch()} />;
  }
  if (!demandLinks.data) {
    return null;
  }

  const groups = demandLinks.data.solutions;
  const totalLinks = groups.reduce((n, g) => n + g.links.length, 0);
  const linkedSolutions = groups.filter((g) => g.links.length > 0).length;

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center gap-3 pb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
          <Link2 className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <CardTitle className="text-sm">Demand links</CardTitle>
          <p className="text-xs text-muted-foreground">
            Auto-synced from NOS — each Solution's demands scope its guidance forecast and actuals.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          <RefreshCw
            className={refresh.isPending ? "mr-2 h-3.5 w-3.5 animate-spin" : "mr-2 h-3.5 w-3.5"}
          />
          {refresh.isPending ? "Syncing…" : "Sync now"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          {totalLinks} demand{totalLinks === 1 ? "" : "s"} linked across {linkedSolutions} Solution
          {linkedSolutions === 1 ? "" : "s"}.
        </p>
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Solutions yet — import Solutions first, then sync demand links.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border/50">
            {groups.map((g) => (
              <li key={g.solutionId} className="px-3 py-2.5">
                <div className="text-sm font-medium">
                  {g.accountName} <span className="text-muted-foreground">›</span> {g.solutionName}
                </div>
                {g.links.length === 0 ? (
                  <p className="mt-1 text-xs text-muted-foreground">No linked demands.</p>
                ) : (
                  <ul className="mt-1.5 space-y-1">
                    {g.links.map((link) => (
                      <li
                        key={link.id}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2.5 py-1.5"
                      >
                        <span className="truncate text-xs">
                          {link.demandName ?? link.nymblDemandId}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {link.lastSyncedAt
                            ? `synced ${new Date(link.lastSyncedAt).toLocaleDateString()}`
                            : "never synced"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
